import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Crown, Clock } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { ImageUploader } from "@/components/ImageUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AdminPredictions } from "@/pages/admin/AdminPredictions";
import { AdminPeriods } from "@/pages/admin/AdminPeriods";

export function AdminVip() {
  const [cfg, setCfg] = useState(null);
  const [game, setGame] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/vip-config").then((r) => setCfg({ group_link: r.data.group_link || "", group_logo: r.data.group_logo || null }));
    api.get("/admin/game-settings").then((r) => setGame({ reset_hour: r.data.reset_hour ?? 9 }));
  }, []);

  const saveCfg = async () => {
    setSaving(true);
    try { await api.put("/admin/vip-config", cfg); toast.success("Configurazione VIP salvata"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const saveGame = async () => {
    try { await api.put("/admin/game-settings", { reset_hour: Number(game.reset_hour) }); toast.success("Orario reset salvato"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!cfg || !game) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-green-500" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold uppercase text-zinc-950">
          <Crown className="h-6 w-6 text-yellow-500" /> Sezione VIP
        </h2>
        <p className="text-sm text-zinc-500">Gestisci gruppo privato, pronostici e classifiche VIP. Abilita gli utenti VIP dalla scheda "Utenti".</p>
      </div>

      <Tabs defaultValue="config">
        <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
          <TabsTrigger data-testid="admin-vip-tab-config" value="config" className="rounded-full border border-slate-200 bg-white px-4 py-2 font-bold data-[state=active]:bg-zinc-950 data-[state=active]:text-yellow-400">Impostazioni</TabsTrigger>
          <TabsTrigger data-testid="admin-vip-tab-preds" value="preds" className="rounded-full border border-slate-200 bg-white px-4 py-2 font-bold data-[state=active]:bg-zinc-950 data-[state=active]:text-yellow-400">Pronostici VIP</TabsTrigger>
          <TabsTrigger data-testid="admin-vip-tab-periods" value="periods" className="rounded-full border border-slate-200 bg-white px-4 py-2 font-bold data-[state=active]:bg-zinc-950 data-[state=active]:text-yellow-400">Classifiche VIP</TabsTrigger>
        </TabsList>

        <TabsContent value="config">
          <div className="max-w-lg space-y-5">
            <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
              <p className="font-display text-lg font-black uppercase text-zinc-950">Gruppo privato</p>
              <ImageUploader label="Logo gruppo privato" value={cfg.group_logo} onChange={(v) => setCfg({ ...cfg, group_logo: v })} testId="vip-group-logo-upload" />
              <div className="space-y-1.5">
                <Label>Link gruppo privato</Label>
                <Input data-testid="vip-group-link-input" value={cfg.group_link} onChange={(e) => setCfg({ ...cfg, group_link: e.target.value })} placeholder="https://t.me/+..." className="rounded-xl" />
              </div>
              <Button data-testid="vip-config-save" onClick={saveCfg} disabled={saving} className="w-full rounded-full bg-zinc-950 font-bold text-yellow-400 hover:bg-zinc-800">
                <Save className="mr-1 h-4 w-4" /> {saving ? "Salvataggio..." : "Salva gruppo privato"}
              </Button>
            </div>

            <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5">
              <p className="flex items-center gap-2 font-display text-lg font-black uppercase text-zinc-950"><Clock className="h-5 w-5 text-green-500" /> Gioco "Trova il premio"</p>
              <div className="space-y-1.5">
                <Label>Orario reset giornaliero (ora italiana, 0–23)</Label>
                <Input data-testid="game-reset-hour-input" type="number" min={0} max={23} value={game.reset_hour}
                  onChange={(e) => setGame({ reset_hour: e.target.value })} className="rounded-xl" />
              </div>
              <Button data-testid="game-settings-save" onClick={saveGame} className="w-full rounded-full bg-zinc-950 font-bold text-green-400 hover:bg-zinc-800">
                <Save className="mr-1 h-4 w-4" /> Salva orario reset
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="preds"><AdminPredictions vip /></TabsContent>
        <TabsContent value="periods"><AdminPeriods vip /></TabsContent>
      </Tabs>
    </div>
  );
}
